import { useState, useEffect } from 'react'
import { Plus, Trash2, CheckCircle, XCircle, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import { apiClient } from '../../services/api'

interface Webhook {
  id: number
  name: string
  url: string
  events: string[]
  is_active: boolean
  created_at: string
}

interface Delivery {
  id: number
  event_type: string
  response_status: number | null
  success: boolean
  attempt: number
  duration_ms: number | null
  delivered_at: string
}

const ALL_EVENTS = [
  { value: 'task.status_changed', label: 'Task status changed' },
  { value: 'milestone.completed', label: 'Milestone completed' },
  { value: 'ai_insight.created', label: 'AI insight created' },
  { value: 'approval.decision', label: 'Approval decision' },
]

const blank = () => ({ name: '', url: '', events: [] as string[] })

export function WebhooksSection() {
  const [hooks, setHooks] = useState<Webhook[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState(blank())
  const [formErrors, setFormErrors] = useState<{ name?: string; url?: string; events?: string }>({})
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [deliveries, setDeliveries] = useState<Record<number, Delivery[]>>({})
  const [loadingDeliveries, setLoadingDeliveries] = useState<number | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const res = await apiClient.get<Webhook[]>('/api/v1/webhooks/')
      setHooks(res.data)
    } catch {
      toast.error('Failed to load webhooks')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const validate = () => {
    const errs: typeof formErrors = {}
    if (!form.name.trim()) errs.name = 'Name is required'
    if (!form.url.trim()) errs.url = 'URL is required'
    else if (!/^https?:\/\//.test(form.url)) errs.url = 'Must be a valid http(s) URL'
    if (!form.events.length) errs.events = 'Select at least one event'
    setFormErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleCreate = async () => {
    if (!validate()) return
    setCreating(true)
    try {
      const res = await apiClient.post<Webhook>('/api/v1/webhooks/', {
        name: form.name.trim(),
        url: form.url.trim(),
        events: form.events,
      })
      setHooks(prev => [res.data, ...prev])
      setForm(blank())
      setShowCreate(false)
      toast.success('Webhook registered')
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(msg || 'Failed to create webhook')
    } finally {
      setCreating(false)
    }
  }

  const toggleActive = async (hook: Webhook) => {
    try {
      const res = await apiClient.patch<Webhook>(`/api/v1/webhooks/${hook.id}`, {
        is_active: !hook.is_active,
      })
      setHooks(prev => prev.map(h => h.id === hook.id ? res.data : h))
    } catch {
      toast.error('Failed to update webhook')
    }
  }

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(`Delete webhook "${name}"?`)) return
    try {
      await apiClient.delete(`/api/v1/webhooks/${id}`)
      setHooks(prev => prev.filter(h => h.id !== id))
      toast.success('Webhook deleted')
    } catch {
      toast.error('Failed to delete webhook')
    }
  }

  const toggleDeliveries = async (hookId: number) => {
    if (expandedId === hookId) {
      setExpandedId(null)
      return
    }
    setExpandedId(hookId)
    if (!deliveries[hookId]) {
      setLoadingDeliveries(hookId)
      try {
        const res = await apiClient.get<Delivery[]>(`/api/v1/webhooks/${hookId}/deliveries`)
        setDeliveries(prev => ({ ...prev, [hookId]: res.data }))
      } catch {
        toast.error('Failed to load deliveries')
      } finally {
        setLoadingDeliveries(null)
      }
    }
  }

  const toggleEvent = (ev: string) => {
    setForm(f => ({
      ...f,
      events: f.events.includes(ev) ? f.events.filter(e => e !== ev) : [...f.events, ev],
    }))
    if (formErrors.events) setFormErrors(f => ({ ...f, events: undefined }))
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-[#102a43] text-lg">Webhooks</h3>
          <p className="text-sm text-[#627d98] mt-0.5">
            Receive real-time events in Zapier, Power Automate, or your own systems.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(v => !v)}
          className="flex items-center gap-2 px-4 py-2.5 min-h-[44px] bg-[#486581] hover:bg-[#5a7692] text-white rounded-lg transition-colors text-sm font-medium"
          aria-expanded={showCreate}
          aria-controls="create-webhook-form"
        >
          <Plus size={16} aria-hidden="true" />
          Add Webhook
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div
          id="create-webhook-form"
          className="bg-[#f0f4f8] border-2 border-[#bcccdc] rounded-lg p-5 space-y-4"
          role="region"
          aria-label="Register new webhook"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="wh-name" className="block text-sm font-medium text-[#334e68] mb-1">
                Name <span className="text-red-600" aria-hidden="true">*</span>
                <span className="sr-only">(required)</span>
              </label>
              <input
                id="wh-name"
                type="text"
                value={form.name}
                onChange={e => { setForm(f => ({ ...f, name: e.target.value })); if (formErrors.name) setFormErrors(f => ({ ...f, name: undefined })) }}
                onBlur={() => { if (!form.name.trim()) setFormErrors(f => ({ ...f, name: 'Name is required' })) }}
                placeholder="e.g. Zapier task updates"
                className={`w-full border-2 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] ${formErrors.name ? 'border-red-400' : 'border-[#bcccdc]'}`}
                aria-describedby={formErrors.name ? 'wh-name-error' : undefined}
                aria-required="true"
                aria-invalid={!!formErrors.name}
              />
              {formErrors.name && (
                <p id="wh-name-error" className="text-red-600 text-xs mt-1 flex items-center gap-1" role="alert">
                  <AlertCircle size={12} aria-hidden="true" />{formErrors.name}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="wh-url" className="block text-sm font-medium text-[#334e68] mb-1">
                Endpoint URL <span className="text-red-600" aria-hidden="true">*</span>
                <span className="sr-only">(required)</span>
              </label>
              <input
                id="wh-url"
                type="url"
                value={form.url}
                onChange={e => { setForm(f => ({ ...f, url: e.target.value })); if (formErrors.url) setFormErrors(f => ({ ...f, url: undefined })) }}
                onBlur={() => { if (!form.url.trim()) setFormErrors(f => ({ ...f, url: 'URL is required' })) }}
                placeholder="https://hooks.zapier.com/…"
                className={`w-full border-2 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] ${formErrors.url ? 'border-red-400' : 'border-[#bcccdc]'}`}
                aria-describedby={formErrors.url ? 'wh-url-error' : undefined}
                aria-required="true"
                aria-invalid={!!formErrors.url}
              />
              {formErrors.url && (
                <p id="wh-url-error" className="text-red-600 text-xs mt-1 flex items-center gap-1" role="alert">
                  <AlertCircle size={12} aria-hidden="true" />{formErrors.url}
                </p>
              )}
            </div>
          </div>

          <fieldset>
            <legend className="text-sm font-medium text-[#334e68] mb-2">
              Events <span className="text-red-600" aria-hidden="true">*</span>
              <span className="sr-only">(required, select at least one)</span>
            </legend>
            <div className="flex flex-wrap gap-2" role="group" aria-required="true">
              {ALL_EVENTS.map(ev => (
                <label
                  key={ev.value}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 cursor-pointer text-sm transition-colors min-h-[44px] ${
                    form.events.includes(ev.value)
                      ? 'border-[#486581] bg-[#e8f1fb] text-[#243b55]'
                      : 'border-[#bcccdc] bg-white text-[#486581] hover:border-[#829ab1]'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={form.events.includes(ev.value)}
                    onChange={() => toggleEvent(ev.value)}
                    className="sr-only"
                  />
                  {form.events.includes(ev.value) && <CheckCircle size={14} aria-hidden="true" />}
                  {ev.label}
                </label>
              ))}
            </div>
            {formErrors.events && (
              <p className="text-red-600 text-xs mt-1 flex items-center gap-1" role="alert">
                <AlertCircle size={12} aria-hidden="true" />{formErrors.events}
              </p>
            )}
          </fieldset>

          <div className="flex gap-3 justify-end">
            <button
              onClick={() => { setShowCreate(false); setForm(blank()); setFormErrors({}) }}
              className="px-4 py-2.5 min-h-[44px] border-2 border-[#bcccdc] rounded-lg text-sm text-[#334e68] hover:bg-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={creating}
              className="px-4 py-2.5 min-h-[44px] bg-[#486581] hover:bg-[#5a7692] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {creating ? 'Saving…' : 'Register Webhook'}
            </button>
          </div>
        </div>
      )}

      {/* Webhook list */}
      {loading ? (
        <p className="text-sm text-[#829ab1] py-4 text-center" role="status">Loading webhooks…</p>
      ) : hooks.length === 0 ? (
        <div className="text-center py-10 text-[#829ab1] border-2 border-dashed border-[#bcccdc] rounded-lg">
          <p className="text-sm">No webhooks registered yet.</p>
        </div>
      ) : (
        <ul className="space-y-3" aria-label="Registered webhooks">
          {hooks.map(h => (
            <li key={h.id} className="border-2 border-[#bcccdc] rounded-lg bg-white overflow-hidden">
              {/* Row */}
              <div className="flex items-center gap-3 p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-[#102a43]">{h.name}</span>
                    <span className={`px-2 py-0.5 rounded text-xs border font-medium ${h.is_active ? 'bg-green-100 text-green-700 border-green-300' : 'bg-gray-100 text-gray-500 border-gray-300'}`}>
                      {h.is_active ? 'Active' : 'Paused'}
                    </span>
                  </div>
                  <p className="text-xs text-[#627d98] mt-0.5 font-mono truncate">{h.url}</p>
                  <p className="text-xs text-[#829ab1] mt-0.5">{h.events.join(' · ')}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => toggleActive(h)}
                    aria-label={h.is_active ? `Pause ${h.name}` : `Activate ${h.name}`}
                    className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-[#f0f4f8] transition-colors text-[#486581]"
                    title={h.is_active ? 'Pause' : 'Resume'}
                  >
                    {h.is_active ? <XCircle size={16} aria-hidden="true" /> : <CheckCircle size={16} aria-hidden="true" />}
                  </button>
                  <button
                    onClick={() => toggleDeliveries(h.id)}
                    aria-label={expandedId === h.id ? `Collapse delivery log for ${h.name}` : `Expand delivery log for ${h.name}`}
                    aria-expanded={expandedId === h.id}
                    className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-[#f0f4f8] transition-colors text-[#486581]"
                  >
                    {expandedId === h.id ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
                  </button>
                  <button
                    onClick={() => handleDelete(h.id, h.name)}
                    aria-label={`Delete webhook ${h.name}`}
                    className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={16} aria-hidden="true" />
                  </button>
                </div>
              </div>

              {/* Delivery log */}
              {expandedId === h.id && (
                <div className="border-t-2 border-[#bcccdc] bg-[#f0f4f8] p-3" role="region" aria-label={`Delivery log for ${h.name}`}>
                  {loadingDeliveries === h.id ? (
                    <p className="text-xs text-[#829ab1] text-center py-2" role="status">Loading…</p>
                  ) : !deliveries[h.id]?.length ? (
                    <p className="text-xs text-[#829ab1] text-center py-2">No deliveries yet.</p>
                  ) : (
                    <table className="w-full text-xs" aria-label="Delivery history">
                      <thead>
                        <tr className="text-[#627d98] text-left">
                          <th scope="col" className="pb-1 pr-3 font-medium">Event</th>
                          <th scope="col" className="pb-1 pr-3 font-medium">Status</th>
                          <th scope="col" className="pb-1 pr-3 font-medium">Duration</th>
                          <th scope="col" className="pb-1 font-medium">Delivered</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deliveries[h.id].map(d => (
                          <tr key={d.id} className="border-t border-[#bcccdc]">
                            <td className="py-1.5 pr-3 font-mono">{d.event_type}</td>
                            <td className="py-1.5 pr-3">
                              <span className={`flex items-center gap-1 ${d.success ? 'text-green-600' : 'text-red-500'}`}>
                                {d.success
                                  ? <><CheckCircle size={11} aria-hidden="true" />{d.response_status}</>
                                  : <><XCircle size={11} aria-hidden="true" />{d.response_status ?? 'Error'}</>
                                }
                              </span>
                            </td>
                            <td className="py-1.5 pr-3">{d.duration_ms != null ? `${d.duration_ms}ms` : '—'}</td>
                            <td className="py-1.5 text-[#829ab1]">{new Date(d.delivered_at).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
